<template>
  <div v-if="active" class="gs-pane">
    <ul v-if="loading" class="gs-results-list gs-skeleton" aria-hidden="true">
      <li v-for="i in 3" :key="`skeleton-${i}`">
        <div class="gs-result-item gs-navigation-section">
          <div class="gs-navigation-icon" aria-hidden="true">
            <zm-skeleton-item variant="circle" style="width: 40px; height: 40px" />
          </div>
          <div class="gs-navigation-content">
            <p class="gs-navigation-name">
              <zm-skeleton-item variant="text" :percent="80" />
            </p>
            <p class="gs-navigation-path">
              <zm-skeleton-item variant="text" :percent="60" />
            </p>
          </div>
        </div>
      </li>
    </ul>
    <ul
      v-else-if="routeList.length > 0"
      :id="listboxId"
      class="gs-results-list"
      role="listbox"
      tabindex="0"
      :aria-activedescendant="activeDescendantId"
      :aria-label="t('aria.navigationResults', [routeList.length])">
      <li v-for="(route, index) in routeList" :key="route.id" role="presentation">
        <a
          v-bind="optionAttrs(index)"
          class="gs-result-item gs-navigation-section"
          :class="{ 'is-active': index === activeIndex }"
          href="javascript: void(0)"
          @mouseenter="emit('update:activeIndex', index)"
          @click="onNavigation(route)">
          <div
            v-if="route.icon?.render"
            class="gs-navigation-icon"
            :class="{ 'is-product-icon': route.id?.startsWith('waffle-menu-item-') }"
            aria-hidden="true">
            <zm-icon>
              <component :is="route.icon.render" />
            </zm-icon>
          </div>
          <div class="gs-navigation-content">
            <p class="gs-navigation-name">
              <zm-highlight-text
                :key="`${route.label}::${activeQuery}`"
                :content="route.label || ''"
                :highlight="highlightKeywords" />
            </p>
            <p class="gs-navigation-path">
              <zm-highlight-text
                :key="`${route.id}-path::${activeQuery}`"
                :content="route.breadcrumbs.join(' > ')"
                :highlight="highlightKeywords" />
            </p>
          </div>
        </a>
      </li>
    </ul>
    <div v-else class="gs-empty" role="status">
      <img src="/assets/search-empty.png" width="200" height="200" alt="" />
      <p class="empty-main">{{ $t('searchEmpty') }}</p>
      <p class="empty-sub">{{ $t('noNavigation') }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { ZmHighlightText, ZmIcon, ZmSkeletonItem } from '@zoom/zoom-ui-vue3'
import { transferMenu2Document } from './menus-to-documents'
import { searchService, convertMenusToSearchDocs } from './navigation-search'
import { createMenuIconMap } from './menu-utils'
import { toHighlightKeywords } from './highlight.js'
import { trackEvent } from '@/shared/event-tracker'
import { rememberSearchResultNavigation } from './search-navigation-focus'
import { get } from '../../../shared/api-helper'

const props = defineProps({
  active: { type: Boolean, default: true },
  activeIndex: { type: Number, default: -1 },
  listboxId: { type: String, default: '' },
  optionIdPrefix: { type: String, default: 'gs-option-navigation' },
  searchLevel: {
    type: String,
    default: 'admin'
  }
})

const emit = defineEmits(['update:activeIndex'])

const { t } = useI18n()

const routeList = ref([])
const loading = ref(false)
const initialized = ref(false)
const menuIconMap = ref(new Map())
const activeQuery = ref('')
const highlightKeywords = computed(() => toHighlightKeywords(activeQuery.value))
let abortController = null
const retryTimer1 = ref(null)
const retryTimer2 = ref(null)

const cancel = () => {
  abortController?.abort()
  abortController = null
  loading.value = false
}

const MENU_LOADED_EVENTS = ['admin-side-menu-loaded', 'personal-side-menu-loaded']

let waffleMenuItems = []
let waffleMenuPromise = null

const loadWaffleMenuItems = () => {
  if (!waffleMenuPromise) {
    waffleMenuPromise = get('/waffle/available_items')()
      .then((result) => {
        waffleMenuItems = (result?.groupItems?.Product ?? []).map((item) => ({
          id: `waffle-menu-item-${item.id}`,
          label: item.name,
          labelEn: item.nameEn,
          url: item.url,
          breadcrumbs: ['Product', item.name],
          keywords: ['Product']
        }))
      })
      .catch((error) => {
        console.error('❌ Failed to load waffle menu items:', error)
        waffleMenuPromise = null
      })
  }
  return waffleMenuPromise
}

// Serialize init calls so concurrent triggers (mount, menu events, search) don't interleave
// resetDatabase/insertMultiple on the shared search db
let initQueue = Promise.resolve(false)
const initSearchService = (force = false) => {
  initQueue = initQueue.then(() => doInitSearchService(force))
  return initQueue
}

const doInitSearchService = async (force = false) => {
  if (!window.sideMenu?.menusData) {
    console.warn('⚠️ window.sideMenu.menusData is not available yet')
    return false
  }

  if (!Array.isArray(window.sideMenu.menusData) || window.sideMenu.menusData.length === 0) {
    console.warn('⚠️ window.sideMenu.menusData is empty')
    return false
  }

  if (!force && initialized.value && searchService.getDocumentCount() > 0) {
    return true
  }

  try {
    const menuItems = transferMenu2Document(window.sideMenu.menusData)

    if (menuItems.length === 0) {
      console.warn('⚠️ No valid menu items after conversion')
      return false
    }

    // Side menu items come first so they win URL dedupe over waffle items
    const searchDocs = convertMenusToSearchDocs(menuItems.concat(waffleMenuItems))

    if (searchDocs.length === 0) {
      console.warn('⚠️ No valid search documents after conversion')
      return false
    }

    menuIconMap.value = createMenuIconMap()

    await searchService.init(searchDocs)
    initialized.value = true

    return true
  } catch (error) {
    console.error('❌ Failed to initialize navigation search:', error)
    initialized.value = false
    return false
  }
}

const search = async (keyword) => {
  abortController?.abort()
  abortController = null

  const nextQuery = keyword?.trim() ?? ''
  if (!nextQuery) {
    activeQuery.value = ''
    routeList.value = []
    loading.value = false
    return 0
  }

  const controller = new AbortController()
  abortController = controller
  const { signal } = controller
  activeQuery.value = nextQuery

  if (!initialized.value || searchService.getDocumentCount() === 0) {
    let retries = 3
    let success = false

    while (retries > 0 && !success && !signal.aborted) {
      success = await initSearchService()
      if (!success) {
        retries--
        if (retries > 0 && !signal.aborted) {
          await new Promise((resolve, reject) => {
            if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'))
            const timer = setTimeout(resolve, 500)
            signal.addEventListener(
              'abort',
              () => {
                clearTimeout(timer)
                reject(new DOMException('Aborted', 'AbortError'))
              },
              { once: true }
            )
          }).catch(() => {
            retries = 0
          })
        }
      }
    }

    if (signal.aborted) return 0

    if (!success) {
      console.error('❌ Failed to initialize search service after retries')
      return 0
    }
  }

  loading.value = true
  try {
    const result = await searchService.search(nextQuery, {
      limit: 20,
      threshold: 0.2
    })
    if (signal.aborted) return 0

    routeList.value = result.hits
      .map((hit) => hit.document)
      .map((menu) => ({
        ...menu,
        icon: menuIconMap.value.get(menu.id)
      }))
    return routeList.value.length
  } catch (error) {
    if (signal.aborted) return 0
    console.error('❌ Search navigation failed:', error)
    routeList.value = []
    return 0
  } finally {
    if (abortController === controller) loading.value = false
  }
}

const activeDescendantId = computed(() =>
  props.activeIndex >= 0 ? `${props.optionIdPrefix}-${props.activeIndex}` : undefined
)

const optionAttrs = (index) => ({
  id: `${props.optionIdPrefix}-${index}`,
  role: 'option',
  tabindex: -1,
  'aria-selected': index === props.activeIndex,
  'aria-posinset': index + 1,
  'aria-setsize': routeList.value.length
})

const onNavigation = (route) => {
  trackEvent({
    linkText: `${route.breadcrumbs.join(' > ')}`,
    linkUrl: route.url,
    uiLocation: `web portal global search - ${props.searchLevel === 'user' ? 'user' : ''} navigation`
  })
  if (route.id?.startsWith('waffle-menu-item-')) {
    window.open(route.url, '_blank', 'noopener')
  } else {
    rememberSearchResultNavigation()
    setTimeout(() => {
      window.location.href = route.url
    }, 100)
  }
}

const onMenusLoaded = () => {
  initSearchService(true)
}

onMounted(async () => {
  MENU_LOADED_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onMenusLoaded)
  })

  if (props.searchLevel === 'user') {
    // Load waffle items in the background so side menu search isn't blocked; re-index once ready
    loadWaffleMenuItems().then(() => {
      if (waffleMenuItems.length > 0) initSearchService(true)
    })
  }

  let success = await initSearchService()

  if (!success) {
    retryTimer1.value = setTimeout(async () => {
      success = await initSearchService()

      if (!success) {
        retryTimer2.value = setTimeout(async () => {
          await initSearchService()
        }, 2000)
      }
    }, 1000)
  }
})

onBeforeUnmount(() => {
  MENU_LOADED_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, onMenusLoaded)
  })
  if (retryTimer1.value) clearTimeout(retryTimer1.value)
  if (retryTimer2.value) clearTimeout(retryTimer2.value)
})

const activateAt = (index) => {
  const route = routeList.value[index]
  if (route) onNavigation(route)
}

defineExpose({
  search,
  cancel,
  getNavigableCount: () => (loading.value ? 0 : routeList.value.length),
  isLoading: () => loading.value,
  activateAt
})
</script>

<style lang="scss">
.gs-navigation-section {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 8px;
  text-align: left;

  .gs-navigation-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 6px;
    color: var(--zoom-color-text-strong-neutral, #555b62);
    border-radius: 10px;
    background: var(--zoom-color-state-subtle-disable, #f1f4f6);
    .fe-popups-icon {
      font-size: 20px;
    }
    &.is-product-icon {
      background-color: transparent;
      padding: 0;
      .fe-popups-icon {
        color: #0d6bde;
        font-size: 24px;
      }
    }
  }

  .gs-navigation-content {
    flex: 1;
    min-width: 0;
  }
}

.gs-skeleton {
  pointer-events: none;

  .gs-navigation-section {
    cursor: default;
  }
}
</style>
